import { supabase } from '../lib/supabase';

export const employeeSelfServiceService = {
  // ============================================================================
  // EMPLOYEE PROFILE
  // ============================================================================

  async getEmployeeIdByEmail(email) {
    const { data, error } = await supabase
      ?.from('employees')
      ?.select('id')
      ?.eq('email', email)
      ?.single();
    
    if (error) throw error;
    return data?.id;
  },

  async getEmployeeProfile(employeeId) {
    const { data, error } = await supabase
      ?.from('employees')
      ?.select('*')
      ?.eq('id', employeeId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      employeeNumber: data?.employee_number,
      firstName: data?.first_name,
      lastName: data?.last_name,
      email: data?.email,
      phone: data?.phone,
      dateOfBirth: data?.date_of_birth,
      employmentType: data?.employment_type,
      citizenStatus: data?.citizen_status,
      department: data?.department,
      position: data?.position,
      hireDate: data?.hire_date,
      isActive: data?.is_active
    };
  },

  async updatePersonalRecords(employeeId, updates) {
    const { data, error } = await supabase
      ?.from('employees')
      ?.update({
        phone: updates?.phone,
        email: updates?.email
      })
      ?.eq('id', employeeId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      phone: data?.phone,
      email: data?.email
    };
  },

  // ============================================================================
  // PAYSLIPS
  // ============================================================================

  async getEmployeePayslips(employeeId) {
    const { data, error } = await supabase
      ?.from('payslips')
      ?.select(`
        *,
        payroll_details (
          base_pay,
          overtime_pay,
          allowances,
          deductions,
          gross_pay,
          swt_amount,
          employee_super,
          employer_super,
          net_pay,
          hours_worked,
          overtime_hours
        )
      `)
      ?.eq('employee_id', employeeId)
      ?.order('payment_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      payslipNumber: row?.payslip_number,
      payPeriodStart: row?.pay_period_start,
      payPeriodEnd: row?.pay_period_end,
      paymentDate: row?.payment_date,
      payslipStatus: row?.payslip_status,
      generatedAt: row?.generated_at,
      details: row?.payroll_details ? {
        basePay: row?.payroll_details?.base_pay,
        overtimePay: row?.payroll_details?.overtime_pay,
        allowances: row?.payroll_details?.allowances,
        deductions: row?.payroll_details?.deductions,
        grossPay: row?.payroll_details?.gross_pay,
        swtAmount: row?.payroll_details?.swt_amount,
        employeeSuper: row?.payroll_details?.employee_super,
        employerSuper: row?.payroll_details?.employer_super,
        netPay: row?.payroll_details?.net_pay,
        hoursWorked: row?.payroll_details?.hours_worked,
        overtimeHours: row?.payroll_details?.overtime_hours
      } : null
    })) || [];
  },

  // ============================================================================
  // LEAVE MANAGEMENT
  // ============================================================================

  async getLeaveBalances(employeeId) {
    const currentYear = new Date()?.getFullYear();
    const { data, error } = await supabase
      ?.from('leave_entitlements')
      ?.select('*')
      ?.eq('employee_id', employeeId)
      ?.eq('year', currentYear);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      leaveType: row?.leave_type,
      accruedDays: row?.accrued_days,
      usedDays: row?.used_days,
      balanceDays: row?.balance_days,
      year: row?.year
    })) || [];
  },

  async getLeaveRequests(employeeId) {
    const { data, error } = await supabase
      ?.from('leave_requests')
      ?.select('*')
      ?.eq('employee_id', employeeId)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      leaveType: row?.leave_type,
      startDate: row?.start_date,
      endDate: row?.end_date,
      daysRequested: row?.days_requested,
      leaveStatus: row?.leave_status,
      reason: row?.reason,
      medicalCertificateRequired: row?.medical_certificate_required,
      medicalCertificateProvided: row?.medical_certificate_provided,
      approvedBy: row?.approved_by,
      approvedAt: row?.approved_at,
      createdAt: row?.created_at
    })) || [];
  },

  async submitLeaveRequest(employeeId, request) {
    const { data, error } = await supabase
      ?.from('leave_requests')
      ?.insert({
        employee_id: employeeId,
        leave_type: request?.leaveType,
        start_date: request?.startDate,
        end_date: request?.endDate,
        days_requested: request?.daysRequested,
        reason: request?.reason,
        medical_certificate_required: request?.medicalCertificateRequired || false
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      leaveType: data?.leave_type,
      startDate: data?.start_date,
      endDate: data?.end_date,
      daysRequested: data?.days_requested,
      leaveStatus: data?.leave_status
    };
  },

  async cancelLeaveRequest(requestId) {
    const { data, error } = await supabase
      ?.from('leave_requests')
      ?.update({ leave_status: 'cancelled' })
      ?.eq('id', requestId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      leaveStatus: data?.leave_status
    };
  }
};

export default employeeSelfServiceService;
